import React from 'react'
import { useTranslation } from 'react-i18next'
import { formatDate, LOCALE_MAP } from 'utils/locale-formatting'
import styled from 'styled-components'
import { MaturityBadge } from '../../Shared'

const Section = styled.div``

const HeaderCard = styled.div`
  padding: 24px;
  margin-bottom: 16px;
  border-radius: 14px;
  background: white;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
`

const HeaderContent = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  margin-bottom: 14px;
`

const OrgIconBox = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: var(--vo-capri-50);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

const HeaderText = styled.div``

const OrgName = styled.h2`
  font-family: 'Rubik', sans-serif;
  margin: 0 0 4px;
  font-size: 20px;
  font-weight: 700;
  color: var(--vo-ink);
`

const OrgDescription = styled.p`
  font-family: 'Lato', sans-serif;
  margin: 0;
  font-size: 13px;
  color: var(--vo-slate);
  line-height: 1.55;
`

const LastUpdated = styled.p`
  font-family: 'Lato', sans-serif;
  margin: 0;
  font-size: 12px;
  color: var(--vo-steel);
`

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
  margin-bottom: 16px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`

const StatCard = styled.div`
  padding: 20px;
  border-radius: 14px;
  text-align: center;
  background: white;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
`

const StatIcon = styled.div`
  margin-bottom: 8px;
`

const StatValue = styled.div`
  font-family: 'Rubik', sans-serif;
  font-size: 32px;
  font-weight: 800;
  color: ${({ $color }) => $color || 'var(--vo-celeste)'};
  margin: 0 0 4px;
  letter-spacing: -.02em;
`

const StatLabel = styled.div`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-slate);
  margin-top: 4px;
`

const FrameworksSection = styled.div`
  padding: 24px;
  border-radius: 14px;
  background: white;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
`

const SectionTitle = styled.h3`
  font-family: 'Rubik', sans-serif;
  margin: 0 0 16px 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--vo-ink);
`

const FrameworksGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
`

const FrameworkItem = styled.div`
  padding: 12px 16px;
  border: 1px solid var(--vo-cloud);
  border-radius: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const FrameworkName = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: var(--vo-ink);
`

const BadgeWrapper = styled.div`
  margin-bottom: 8px;
`

const BusinessSvg = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="var(--vo-celeste)">
    <path d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z"/>
  </svg>
)

const SecuritySvg = () => (
  <svg width="36" height="36" viewBox="0 0 24 24" fill="var(--vo-celeste)">
    <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"/>
  </svg>
)

const PolicySvg = () => (
  <svg width="36" height="36" viewBox="0 0 24 24" fill="var(--vo-success)">
    <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm-3.06 16L7.4 14.46l1.41-1.41 2.12 2.12 4.24-4.24 1.41 1.41L10.94 18zM13 9V3.5L18.5 9H13z"/>
  </svg>
)

export const OverviewTab = ({ organization, frameworks = [], governanceStructure }) => {
  const { t, i18n } = useTranslation('transparency')
  const locale = LOCALE_MAP[i18n.language] ?? 'en-US'
  const frameworkCount = frameworks?.length || 0
  const categoryCount = governanceStructure?.categories?.length || 0

  return (
    <Section>
      <HeaderCard>
        <HeaderContent>
          <OrgIconBox>
            <BusinessSvg />
          </OrgIconBox>
          <HeaderText>
            <OrgName>{organization?.name}</OrgName>
            {organization?.description && (
              <OrgDescription>{organization.description}</OrgDescription>
            )}
          </HeaderText>
        </HeaderContent>
        {organization?.lastUpdated && (
          <LastUpdated>
            {t('protected.overview.lastUpdated', { date: formatDate(organization.lastUpdated, locale) })}
          </LastUpdated>
        )}
      </HeaderCard>

      <CardsGrid>
        <StatCard>
          <StatIcon><SecuritySvg /></StatIcon>
          <StatValue>{frameworkCount}</StatValue>
          <StatLabel>{t('protected.overview.frameworks')}</StatLabel>
        </StatCard>

        <StatCard>
          <StatIcon><PolicySvg /></StatIcon>
          <StatValue $color="var(--vo-success)">{categoryCount}</StatValue>
          <StatLabel>{t('protected.overview.governanceCategories')}</StatLabel>
        </StatCard>

        {governanceStructure?.overallPosture && (
          <StatCard>
            <BadgeWrapper>
              <MaturityBadge label={governanceStructure.overallPosture.maturityLabel} />
            </BadgeWrapper>
            <StatLabel>{t('protected.overview.overallMaturity')}</StatLabel>
          </StatCard>
        )}
      </CardsGrid>

      {frameworks && frameworks.length > 0 && (
        <FrameworksSection>
          <SectionTitle>{t('protected.overview.frameworksInUse')}</SectionTitle>
          <FrameworksGrid>
            {frameworks.slice(0, 6).map((fw, index) => (
              <FrameworkItem key={fw.frameworkId || index}>
                <FrameworkName>{fw.name}</FrameworkName>
                <MaturityBadge label={fw.maturityLabel} />
              </FrameworkItem>
            ))}
          </FrameworksGrid>
        </FrameworksSection>
      )}
    </Section>
  )
}
